import { useUpdateSearchParamUrl } from 'hooks/hook';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SIZE_LOCATIONS_PAGE } from 'const/paging';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFloorPlan, getLocations } from 'services/locations';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { isNil, omitBy } from 'lodash';
import { RoomInfo } from 'types/room';
import { isVerticalLocationLevel, renderTitle } from 'utils/renderTitleLocation';
import * as qs from 'qs';
import { getFloorPlantype } from 'types/floorplan';
import { Location } from 'types/loctions';
export const useInjection = () => {
  const { clientId, locationId, clientName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useUpdateSearchParamUrl();
  const [locations, setLocations] = useState<getFloorPlantype>();
  const [locationdata, setLocationsdata] = useState<Location[]>([]);
  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [searchType, setSearchType] = useState<string>('partial-text');
  const [title, setTitle] = useState<{
    title: string;
    textButton: string;
    level: number;
  }>({
    title: '',
    textButton: '',
    level: 0
  });

  const isFreeSearch = useMemo(() => {
    return !!searchParams.get('searchText');
  }, [searchParams]);

  const header = [{ label: 'Location Name' }, { label: 'Actions', width: 420 }];
  const handleGetInitLocations = useCallback(async () => {
    if (!clientId) return;
    if (!locationId) return;
    const params = {
      size: SIZE_LOCATIONS_PAGE,
      page: 1,
      parentId: locationId ? locationId : undefined,
      clientId: clientId ? clientId : undefined
    };
    const datalocation = (await getLocations(params)) as {
      locations: Location[];
      room: RoomInfo;
      total: number;
    };
    setLocationsdata(datalocation.locations);
    setRoomInfo(datalocation.room);
    const levelLocationInit =
      datalocation?.room?.find((item) => item.id === Number(locationId))?.level || 0;
    setTitle(
      renderTitle(
        datalocation.locations[0]?.level ? datalocation.locations[0]?.level : levelLocationInit + 1
      )
    );
    const payload = {
      id: Number(locationId)
    };
    const data = await getFloorPlan(payload);
    setLocations(data.data);
    const element = document.getElementById('id-table-clients');
    if (element) {
      element.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [clientId, locationId, searchParams, searchType]);
  const paths: RoomInfo = useMemo(() => {
    return [
      { name: 'Inspection', level: 999, onClick: () => navigate('/inspection') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/inspection/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          const href = isVerticalLocationLevel(item.level)
            ? `/inspection/${clientName}/${clientId}/location/${item.id}`
            : `/inspection/${clientName}/${clientId}/location/${item.id}`;
          return {
            ...item,
            onClick: () => navigate(href)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);

  const { asyncCallback: asyncGetInitLocations } = useAsyncCallback(handleGetInitLocations, []);

  const handleSearch = useCallback(
    (searchText: string) => {
      setSearchParams(omitBy({ searchText }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  const filterByTypeAccess = useCallback(
    (type: string) => {
      setSearchParams(omitBy({ type }, isNil) as { [key: string]: string });
    },
    [setSearchParams]
  );

  useEffect(() => {
    asyncGetInitLocations();
  }, [asyncGetInitLocations]);

  const handleBack = useCallback(() => {
    if (!locationId) {
      return navigate('/inspection');
    }

    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 1) {
      pathSegments.pop();
      navigate(pathSegments.join('/'));
    }
  }, [location.pathname, locationId, navigate]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      let order = 'asc';
      const sortFieldOld = searchParams.get('sortBy');
      const orderOld = searchParams.get('order');
      if (sortFieldOld === sortField) {
        if (orderOld === 'asc') {
          order = 'desc';
        } else {
          order = 'asc';
        }
      }
      setSearchParams(
        omitBy({ sortBy: sortField, order: order }, isNil) as { [key: string]: string }
      );
    },
    [searchParams, setSearchParams]
  );
  const onCreate = (level: number) => {
    const query = qs.stringify({
      level
    });
    const path = location.pathname.replace('/plans', '');
    navigate(`${path}/upload?${query}`, {
      state: { mode: 'add' }
    });
  };

  const onClickAction = useCallback(
    ({ href }: { href: string; locationId: number }) => {
      navigate(href);
    },
    [navigate]
  );
  const listButton = useMemo(() => {
    const buttons = [];
    buttons.push({
      textButton: 'Add Floor Plan',
      onClick: () => onCreate(4),
      type: 'primary'
    });

    return buttons;
  }, [onCreate, title.level, title.textButton]);

  const displayTitle = 'Floor Plans';
  const floorplandata = locations?.floorPlan;

  return {
    locations,
    floorplandata,
    setLocations,
    handleSearch,
    displayTitle,
    onSortHeader,
    filterByTypeAccess,
    searchParams,
    pathName: location.pathname,
    handleBack,
    onCreate,
    listButton,
    paths,
    onClickAction,
    clientId,
    clientName,
    title,
    header,
    isFreeSearch,
    searchType,
    setSearchType,
    asyncGetInitLocations
  };
};
